// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ERROR_CODE_MAPPING } from '@/api/constants';
import { InvoiceDetail } from '@/billing/api/types';
import { useMarkInvoiceAsSent } from '@/billing/hooks/use-mark-invoice-as-sent';
import {
  MarkInvoiceAsSentFormData,
  markInvoiceAsSentFormSchema,
} from '@/billing/schemas/mark-invoice-as-sent-schema';
import { FormDateTimePicker } from '@/components/form-date-time-picker';
import { KnownErrorSnackbar } from '@/components/known-error-snackbar';
import { SupportSnackbar } from '@/components/support-snackbar';
import { zodResolver } from '@hookform/resolvers/zod';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';

// Codes where we surface the backend message inline via the known-error
// snackbar instead of the generic SupportSnackbar. Everything else is
// treated as unexpected and pairs with Sentry.
const KNOWN_ERROR_CODES = new Set<number>([
  ERROR_CODE_MAPPING.invoice_not_sendable,
  ERROR_CODE_MAPPING.invoice_sent_at_future,
]);

interface Props {
  isOpen: boolean;
  invoice: InvoiceDetail;
  onClose: () => void;
  onSuccess?: () => void;
}

export function MarkInvoiceAsSentDialog(props: Props) {
  const [knownError, setKnownError] = useState<string | null>(null);
  const [showSupportSnackbar, setShowSupportSnackbar] = useState(false);

  const { control, handleSubmit, reset } = useForm<MarkInvoiceAsSentFormData>({
    resolver: zodResolver(markInvoiceAsSentFormSchema),
    defaultValues: { sentAt: DateTime.local() },
  });

  // Reset the form each time the dialog transitions closed → open so users
  // always see the current wall-clock time as the default. Also clear any
  // stale snackbar state so a previously fired support snackbar doesn't
  // linger past the next dialog open.
  useEffect(() => {
    if (props.isOpen) {
      reset({ sentAt: DateTime.local() });
      setKnownError(null);
      setShowSupportSnackbar(false);
    }
  }, [props.isOpen, reset]);

  // Any edit to the sent-at value clears the prior submission's backend
  // error — the user is actively trying to correct the failure and the
  // stale message would mislead them. Zod validation (the inline
  // helperText) is handled separately by react-hook-form's default
  // reValidateMode: 'onChange' after first submit, so those errors
  // auto-update on each keystroke without any work here.
  const sentAtValue = useWatch({ control, name: 'sentAt' });
  useEffect(() => {
    setKnownError(null);
    setShowSupportSnackbar(false);
  }, [sentAtValue]);

  const mutation = useMarkInvoiceAsSent({
    invoiceKey: props.invoice.key,
    onSuccess: () => {
      props.onClose();
      props.onSuccess?.();
    },
    onError: (message, apiStatusCode, httpStatusCode) => {
      // Known validation errors (e.g. sent_at in the future, invoice not
      // sendable) keep the dialog open so the user can adjust the date
      // and retry; the inline known-error snackbar carries the message.
      if (KNOWN_ERROR_CODES.has(apiStatusCode)) {
        setKnownError(message);
        return;
      }
      // 404 means the invoice is gone — close; there's nothing to retry.
      if (httpStatusCode === 404) {
        props.onClose();
        return;
      }
      // Unexpected errors (5xx / unknown) keep the dialog open. The
      // SupportSnackbar renders above the dialog so the user sees the
      // failure and can retry without re-entering the sent-at value.
      setShowSupportSnackbar(true);
    },
  });

  const close = () => {
    if (mutation.isPending) return;
    props.onClose();
  };

  const onSubmit = (data: MarkInvoiceAsSentFormData) => {
    setKnownError(null);
    mutation.mutate({
      invoiceKey: props.invoice.key,
      sentAt: data.sentAt,
    });
  };

  return (
    <>
      <Dialog fullWidth maxWidth="sm" open={props.isOpen} onClose={close}>
        <DialogTitle
          sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 2 }}
        >
          Mark invoice as sent
        </DialogTitle>

        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent sx={{ py: 3 }}>
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                Record that invoice{' '}
                <strong>{props.invoice.invoiceNumber}</strong> was sent.
              </Typography>

              <FormDateTimePicker
                name="sentAt"
                control={control}
                label="Sent at"
                maximumDate={DateTime.local().endOf('day')}
                isDisabled={mutation.isPending}
              />
            </Stack>
          </DialogContent>

          <DialogActions
            sx={{
              px: 3,
              py: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Button onClick={close} disabled={mutation.isPending}>
              Cancel
            </Button>
            <LoadingButton
              type="submit"
              variant="contained"
              color="primary"
              loading={mutation.isPending}
            >
              Mark as sent
            </LoadingButton>
          </DialogActions>
        </form>
      </Dialog>

      <KnownErrorSnackbar
        isOpen={knownError !== null}
        message={knownError ?? ''}
        onClose={() => setKnownError(null)}
      />
      <SupportSnackbar
        isOpen={showSupportSnackbar}
        message="An unexpected error occurred while marking the invoice as sent"
        onClose={() => setShowSupportSnackbar(false)}
      />
    </>
  );
}
